import type { DiagramKey } from '@/components/learn/diagrams'

export type GuideSection = {
    heading: string
    paragraphs: string[]
    steps?: string[]
}

export type Guide = {
    slug: string
    title: string
    summary: string
    minutes: number
    level: string
    diagram: DiagramKey
    caption: string
    sections: GuideSection[]
}

export const GUIDES: Guide[] = [
    {
        slug: 'find-the-north-star',
        title: 'How to find the North Star',
        summary: 'Two stars in the Plough point straight to it. Once you can find Polaris, you will never be lost under a clear sky.',
        minutes: 4,
        level: 'First night',
        diagram: 'north-star',
        caption: 'Follow Merak through Dubhe, about five times the gap between them, to Polaris.',
        sections: [
            {
                heading: 'Why it matters',
                paragraphs: [
                    'Polaris sits almost exactly above the Earth’s north pole, so while every other star wheels around the sky through the night, it stays put. Face it and you are facing north.',
                    'It is not the brightest star in the sky, which surprises most people. It is a modest, steady point of light, about as bright as the stars of the Plough.'
                ]
            },
            {
                heading: 'Finding it',
                paragraphs: ['On any clear night from Britain the Plough is above the horizon somewhere, though it may be standing on its handle in autumn or hanging upside down in spring.'],
                steps: [
                    'Find the Plough: seven bright stars in the shape of a saucepan, high in the north on spring evenings and low in the north in autumn.',
                    'Look at the two stars that make the front edge of the pan, Merak at the bottom and Dubhe at the top. These are the pointers.',
                    'Follow the line from Merak through Dubhe and keep going for about five times the gap between them.',
                    'The first moderately bright star you reach, on its own in a fairly empty patch of sky, is Polaris.'
                ]
            },
            {
                heading: 'A bonus: your latitude',
                paragraphs: ['The height of Polaris above the horizon, in degrees, equals your latitude. From our roof it stands 51 degrees up, a little over halfway from the horizon to the point overhead. Sailors used exactly this to find their way for centuries.']
            }
        ]
    },
    {
        slug: 'photograph-the-moon-with-a-phone',
        title: 'Photograph the Moon with your phone',
        summary: 'Hold the phone to a telescope or binocular eyepiece and turn the brightness down. The craters will surprise you.',
        minutes: 5,
        level: 'Any telescope',
        diagram: 'moon-phone',
        caption: 'Line the phone lens up with the eyepiece, then lower the exposure.',
        sections: [
            {
                heading: 'Pick the right Moon',
                paragraphs: ['A full Moon looks flat, because the Sun is shining straight down on it. A half Moon or a crescent is far more dramatic: along the terminator, the line between lunar day and night, the low sunlight throws long shadows from every crater wall.']
            },
            {
                heading: 'Taking the picture',
                paragraphs: ['This works through any telescope, through binoculars on a tripod, and through the telescopes on our roof deck, where we keep phone adapters for every eyepiece.'],
                steps: [
                    'Use your phone’s main camera at 1x, not the zoom lens.',
                    'Hold the lens a few millimetres from the eyepiece, straight on, until the bright circle fills the screen. An adapter makes this much easier.',
                    'Tap the Moon on the screen to focus, then slide the exposure down until the bright areas show detail instead of white.',
                    'Take a burst of photos and keep the sharpest: the air shimmers, and one in ten will catch a steady moment.'
                ]
            },
            {
                heading: 'Without a telescope',
                paragraphs: ['On a phone alone, use the longest zoom, rest the phone on something solid, and lower the exposure. You will not see craters, but a crescent Moon over a dark landscape makes a lovely picture.']
            }
        ]
    },
    {
        slug: 'what-a-light-year-really-is',
        title: 'What a light-year really is',
        summary: 'Not a time but a distance: how far light travels in a year. Here is how to make the number mean something.',
        minutes: 6,
        level: 'Big ideas',
        diagram: 'light-year',
        caption: 'Light from the Moon is 1.3 seconds old. From Andromeda, 2.5 million years.',
        sections: [
            {
                heading: 'The speed of light',
                paragraphs: ['Light travels about 300,000 kilometres every second. In one second it could go round the Earth seven and a half times. In a year it covers about 9.5 million million kilometres, and that distance is a light-year.']
            },
            {
                heading: 'Looking back in time',
                paragraphs: [
                    'Because light takes time to arrive, everything you see is in the past. The Moon is 1.3 seconds ago. The Sun is eight minutes ago: if it vanished, you would not know for eight minutes.',
                    'The nearest star beyond the Sun is over four years ago. Some of the stars you can see on a dark night from our roof are thousands of years ago, and the Andromeda Galaxy, the most distant thing most people can see with their own eyes, is two and a half million years ago.'
                ]
            },
            {
                heading: 'A way to picture it',
                paragraphs: ['If the Sun were a grapefruit in the middle of the planetarium dome, the Earth would be a grain of salt about eleven metres away. The nearest star would be another grapefruit about three thousand kilometres away, somewhere in Russia.']
            }
        ]
    },
    {
        slug: 'choosing-your-first-telescope',
        title: 'Choosing your first telescope',
        summary: 'Ignore the magnification on the box. What matters is the width of the opening and how steady the stand is.',
        minutes: 7,
        level: 'Buying advice',
        diagram: 'telescope',
        caption: 'A wider opening gathers more light: a 250 mm mirror collects ten times what an 80 mm lens does.',
        sections: [
            {
                heading: 'Start with binoculars',
                paragraphs: ['A pair of 10x50 binoculars, the kind many people already own, shows the craters of the Moon, the moons of Jupiter, star clusters and the Andromeda Galaxy. They also teach you your way around the sky, which every telescope owner needs.']
            },
            {
                heading: 'What actually matters',
                paragraphs: ['The aperture, the width of the main lens or mirror, decides how much light you collect and how much detail you can see. Magnification is set by the eyepiece and is easy to change; a box promising 500x is a warning sign.'],
                steps: [
                    'Aperture first: 130 mm or more if you want galaxies and nebulae.',
                    'A steady mount: a wobbly stand makes any telescope frustrating. Simple Dobsonian bases are steady and cheap.',
                    'Something you will carry outside: the best telescope is the one you actually use.',
                    'Leave astrophotography for later. It needs a very different, much more expensive set-up.'
                ]
            },
            {
                heading: 'Try before you buy',
                paragraphs: ['Come to a telescope night and look through the instruments on our roof deck: a refractor, reflectors of different sizes and a Dobsonian. Our astronomers are happy to talk you through what would suit you, and nobody is selling anything.']
            }
        ]
    },
    {
        slug: 'reading-a-star-chart',
        title: 'Reading a star chart',
        summary: 'Why east is on the left, what the circles mean, and how to hold a chart so the sky matches.',
        minutes: 4,
        level: 'First night',
        diagram: 'star-chart',
        caption: 'Hold the chart above your head with north towards the north, and east falls where it should.',
        sections: [
            {
                heading: 'The chart is a sky seen from below',
                paragraphs: ['A map of the ground is drawn as if you were looking down on it, so east is on the right. A sky chart is drawn as if you were looking up, so east is on the left. Hold it over your head, with the north edge towards north, and it lines up with the sky.']
            },
            {
                heading: 'The parts of the chart',
                paragraphs: [],
                steps: [
                    'The outer circle is the horizon, all the way round.',
                    'The centre is the zenith, the point straight overhead.',
                    'Rings in between show height above the horizon: halfway out is roughly halfway up.',
                    'Larger dots are brighter stars. Start with those and use the lines of the constellations to step to fainter ones.'
                ]
            },
            {
                heading: 'Try it tonight',
                paragraphs: ['Our Sky Tonight chart is drawn exactly this way, for our roof, at any time you choose. Print it, or keep it open on a phone in red-light mode so it does not spoil your night vision.']
            }
        ]
    },
    {
        slug: 'how-your-eyes-adapt-to-the-dark',
        title: 'How your eyes adapt to the dark',
        summary: 'It takes twenty to thirty minutes to see the faintest stars, and one glance at a white screen to lose them.',
        minutes: 4,
        level: 'Every night',
        diagram: 'dark-adaptation',
        caption: 'Your eye wakes up in two stages: quickly for the first few minutes, then slowly for half an hour.',
        sections: [
            {
                heading: 'Two kinds of cell',
                paragraphs: ['Your eye has cones, which see colour and need plenty of light, and rods, which see only in shades of grey but are far more sensitive. In the dark the cones give up within a few minutes and the rods slowly take over.']
            },
            {
                heading: 'Why red light',
                paragraphs: ['Rods are almost blind to deep red light. A dim red torch lets you read a chart or find your feet without resetting the slow work your rods have done. That is why the whole of our site can switch to red, and why we ask everyone on the deck to use red torches only.']
            },
            {
                heading: 'Making the most of it',
                paragraphs: [],
                steps: [
                    'Give yourself twenty minutes away from lights before looking for faint things.',
                    'Put phones into red mode or away entirely. One white screen undoes the wait.',
                    'Look slightly to one side of a faint object: the edge of your vision is richer in rods.',
                    'Breathe slowly and blink. Tired, held-open eyes see less.'
                ]
            }
        ]
    }
]

export const guideBySlug = (slug: string): Guide | undefined => GUIDES.find(guide => guide.slug === slug)
